import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Settings, LogOut, FolderOpen, Store, Inbox, Bell, X, AlertTriangle, AlertCircle, Info, ChevronRight, Sun, Moon, GanttChart, CalendarClock, Copy, BarChart3 } from 'lucide-react';
import axios from 'axios';
import QuickTour from '@/components/QuickTour';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlertIcon = ({ severity }) => {
  switch (severity) {
    case 'Critical': return <AlertTriangle className="w-4 h-4 text-red-500" />;
    case 'Warning': return <AlertCircle className="w-4 h-4 text-amber-500" />;
    default: return <Info className="w-4 h-4 text-blue-500" />;
  }
};

const AlertsDropdown = ({ alerts, onDismiss, onNavigate, onClose }) => {
  const unread = alerts.filter(a => !a.read);

  return (
    <div className="absolute right-0 top-full mt-1 w-80 sm:w-96 max-h-[70vh] overflow-hidden bg-white border border-zinc-200 shadow-lg z-[200] flex flex-col" data-testid="alerts-dropdown">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex-shrink-0">
        <span className="font-heading font-bold text-xs uppercase tracking-wider text-zinc-600">Alerts ({unread.length} new)</span>
        <button onClick={onClose} className="p-1 hover:bg-zinc-200 text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
      </div>
      <div className="overflow-y-auto flex-1">
        {alerts.length === 0 ? (
          <div className="p-6 text-center text-zinc-400 text-sm" data-testid="alerts-empty-state">
            <Bell className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
            No alerts. We'll notify you when something needs attention.
          </div>
        ) : (
          alerts.map((alert) => (
            <div
              key={alert.alert_id}
              onClick={() => onNavigate(alert)}
              data-testid={`alert-item-${alert.alert_id}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigate(alert); }}
              className={`w-full flex items-start gap-3 p-3 text-left border-b border-zinc-100 hover:bg-zinc-50 transition-colors cursor-pointer ${
                !alert.read ? 'bg-amber-50/60' : ''
              }`}
            >
              <div className="mt-0.5 flex-shrink-0"><AlertIcon severity={alert.severity} /></div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${!alert.read ? 'font-semibold text-zinc-900' : 'font-medium text-zinc-700'}`}>{alert.title}</p>
                <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{alert.description}</p>
              </div>
              {!alert.read && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDismiss(alert.alert_id); }}
                  className="flex-shrink-0 mt-0.5 w-5 h-5 bg-primary border border-zinc-300 flex items-center justify-center hover:bg-primary-hover"
                  title="Mark as read"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const alertsRef = useRef(null);

  useEffect(() => {
    fetchAlerts();
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (alertsRef.current && !alertsRef.current.contains(e.target)) {
        setShowAlerts(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/alerts/`, { withCredentials: true });
      setAlerts(res.data);
    } catch { /* ignore */ }
  };

  const handleDismiss = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/dismiss`, {}, { withCredentials: true });
      setAlerts(prev => prev.map(a => a.alert_id === alertId ? { ...a, read: true } : a));
    } catch { /* ignore */ }
  };

  const handleAlertNavigate = (alert) => {
    if (!alert.read) handleDismiss(alert.alert_id);
    setShowAlerts(false);
    if (alert.link_type === 'project' && alert.link_id) {
      navigate(`/projects/${alert.link_id}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const unreadCount = alerts.filter(a => !a.read).length;

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Projects', tour: 'projects' },
    { to: '/timeline', icon: GanttChart, label: 'Timeline' },
    { to: '/inbox', icon: Inbox, label: 'Inbox', tour: 'inbox' },
    { to: '/vendors', icon: Store, label: 'Vendors', tour: 'vendors' },
    { to: '/reports/schedules', icon: CalendarClock, label: 'Reports' },
    { to: '/analytics/products', icon: BarChart3, label: 'Pricing' },
    { to: '/templates', icon: Copy, label: 'Templates' },
    { to: '/settings', icon: Settings, label: 'Settings' }
  ];

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Desktop Sidebar */}
      <aside 
        className="hidden md:flex flex-col w-60 h-screen border-r border-zinc-200 bg-white fixed left-0 top-0 z-40"
        data-testid="desktop-sidebar"
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-zinc-200">
          <h1 className="font-heading text-xl font-bold uppercase tracking-wider flex items-center gap-2 text-zinc-900">
            <div className="w-7 h-7 bg-primary flex items-center justify-center">
              <FolderOpen className="w-4 h-4 text-black" />
            </div>
            Punchlist
          </h1>
        </div>

        {/* User Info */}
        <div className="px-5 py-4 border-b border-zinc-200">
          <p className="font-medium text-sm text-zinc-900 truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs text-zinc-400 font-mono uppercase tracking-wide">{user?.role}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              {...(item.tour ? { 'data-tour': item.tour } : {})}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-xs font-heading font-semibold uppercase tracking-wider transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-zinc-900 border-l-2 border-primary'
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 border-l-2 border-transparent'
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Alerts Button */}
        <div className="relative px-3 mb-2" ref={alertsRef}>
          <button
            onClick={() => setShowAlerts(!showAlerts)}
            data-testid="alerts-bell-desktop"
            data-tour="alerts"
            className="flex items-center gap-3 px-3 py-2.5 text-xs font-heading font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors w-full relative border-l-2 border-transparent"
          >
            <Bell className="w-4 h-4" />
            Alerts
            {unreadCount > 0 && (
              <span className="absolute right-3 top-1.5 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="alerts-badge-desktop">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {showAlerts && (
            <div className="absolute bottom-full left-0 mb-2 z-[200]">
              <AlertsDropdown
                alerts={alerts}
                onDismiss={handleDismiss}
                onNavigate={handleAlertNavigate}
                onClose={() => setShowAlerts(false)}
              />
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <div className="px-3 mb-1">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-desktop"
            className="flex items-center gap-3 px-3 py-2.5 text-xs font-heading font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors w-full border-l-2 border-transparent"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>

        {/* Logout */}
        <div className="px-3 pb-4">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 text-xs font-heading font-semibold uppercase tracking-wider text-zinc-400 hover:text-red-600 transition-colors w-full border-l-2 border-transparent"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header 
        className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 z-40"
        data-testid="mobile-header"
      >
        <h1 className="font-heading text-base font-bold uppercase tracking-wider flex items-center gap-2 text-zinc-900">
          <div className="w-6 h-6 bg-primary flex items-center justify-center">
            <FolderOpen className="w-3.5 h-3.5 text-black" />
          </div>
          Punchlist
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-mobile"
            className="p-1.5 text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <div className="relative" ref={alertsRef}>
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              data-testid="alerts-bell-mobile"
              className="relative p-1.5 text-zinc-600"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="alerts-badge-mobile">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showAlerts && (
              <AlertsDropdown
                alerts={alerts}
                onDismiss={handleDismiss}
                onNavigate={handleAlertNavigate}
                onClose={() => setShowAlerts(false)}
              />
            )}
          </div>
          <p className="font-medium text-xs text-zinc-500 truncate max-w-[100px]" data-testid="mobile-user-name">{user?.name}</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:ml-60 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen main-content">
        {children}
      </main>

      <QuickTour userId={user?.user_id} />

      {/* Mobile Bottom Navigation */}
      <nav 
        className="fixed bottom-0 w-full h-16 bg-white border-t border-zinc-200 flex justify-around items-center md:hidden z-50"
        data-testid="mobile-bottom-nav"
      >
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center h-full px-5 ${
                isActive ? 'text-zinc-900' : 'text-zinc-400'
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className={`text-[10px] font-heading font-semibold uppercase tracking-wider mt-1`}>{item.label}</span>
          </NavLink>
        ))}
        <button
          onClick={handleLogout}
          data-testid="mobile-logout-btn"
          className="flex flex-col items-center justify-center h-full px-5 text-zinc-400"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-[10px] font-heading font-semibold uppercase tracking-wider mt-1">Logout</span>
        </button>
      </nav>
    </div>
  );
};

export default AppLayout;
